import { CriteriaData } from "../../../../types";

interface EnrichedCriteriaData extends CriteriaData {
  responder?: string;
}

interface CriteriaTableProps {
  criteria: EnrichedCriteriaData[];
  selectedIds: number[];
}

export function CriteriaTable({ criteria, selectedIds }: CriteriaTableProps) {
  // Sorting selected items to match selection order
  const activeIds =
    selectedIds.length > 0 ? selectedIds : [criteria[criteria.length - 1]?.id];
  const selectedAssessments = activeIds
    .map((id) => criteria.find((c) => c.id === id))
    .filter((c): c is EnrichedCriteriaData => !!c);

  if (selectedAssessments.length === 0) {
    return (
      <div className="p-4 text-center" style={{ color: "#64748B" }}>
        No criteria data available
      </div>
    );
  }

  const isComparing = selectedAssessments.length === 2;
  const baseAssessment = selectedAssessments[0];
  const compAssessment = selectedAssessments[1];

  return (
    <div className="bg-white rounded-lg p-4">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b" style={{ borderColor: "#E2E8F0" }}>
              <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 w-1/3">
                Criteria
              </th>
              <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: "#F59E0B" }}
                  ></span>
                  {baseAssessment.date}{" "}
                  {baseAssessment.responder && `- ${baseAssessment.responder}`}
                </div>
              </th>
              {isComparing && (
                <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: "#8B5CF6" }}
                    ></span>
                    {compAssessment.date}{" "}
                    {compAssessment.responder &&
                      `- ${compAssessment.responder}`}
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {baseAssessment.assessments.map((criterion, idx) => {
              const compCriterion = compAssessment?.assessments.find(
                (a) => a.name === criterion.name,
              );
              const hasChanged =
                isComparing &&
                compCriterion &&
                criterion.result !== compCriterion.result;

              return (
                <tr
                  key={idx}
                  className={`border-b group transition-colors ${hasChanged ? "bg-amber-50/30" : "hover:bg-slate-50"}`}
                  style={{ borderColor: "#F1F5F9" }}
                >
                  <td className="py-4 px-4 align-top">
                    <div className="text-sm font-bold text-slate-800">
                      {criterion.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 max-w-xs italic">
                      {criterion.description}
                    </div>
                  </td>
                  <td className="py-4 px-4 align-top">
                    <span
                      className="text-xs font-bold px-2 py-1 rounded whitespace-nowrap"
                      style={{
                        color:
                          criterion.result === "Criteria Met"
                            ? "#DC2626"
                            : "#64748B",
                        backgroundColor:
                          criterion.result === "Criteria Met"
                            ? "#FEE2E2"
                            : "#F1F5F9",
                      }}
                    >
                      {criterion.result}
                    </span>
                  </td>
                  {isComparing && compCriterion && (
                    <td className="py-4 px-4 align-top">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs font-bold px-2 py-1 rounded whitespace-nowrap ${hasChanged ? "ring-2 ring-amber-500 ring-offset-1" : ""}`}
                          style={{
                            color:
                              compCriterion.result === "Criteria Met"
                                ? "#DC2626"
                                : "#64748B",
                            backgroundColor:
                              compCriterion.result === "Criteria Met"
                                ? "#FEE2E2"
                                : "#F1F5F9",
                          }}
                        >
                          {compCriterion.result}
                        </span>
                        {hasChanged && (
                          <div className="text-[10px] font-bold text-amber-600 bg-white border border-amber-200 px-1.5 py-0.5 rounded shadow-sm">
                            CHANGED
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
